import React, { useState } from 'react'; 
import { FileText, Send, Check } from 'lucide-react';
import { categories, products } from '../data/mock';
import { toast } from 'sonner';

const RequestQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    projectDescription: '',
    productCategory: '',
    specificProducts: [],
    estimatedQuantity: '',
    projectTimeline: '',
    address: '',
    additionalRequirements: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductSelection = (productId) => {
    setFormData(prev => ({
      ...prev,
      specificProducts: prev.specificProducts.includes(productId)
        ? prev.specificProducts.filter(id => id !== productId)
        : [...prev.specificProducts, productId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('https://formspree.io/f/xzdjlypy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Quote request submitted! We’ll contact you within 24 hours.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }

    } catch (error) {
      toast.error('Submission failed. Check your connection.');
    }

    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      projectType: '',
      projectDescription: '',
      productCategory: '',
      specificProducts: [],
      estimatedQuantity: '',
      projectTimeline: '',
      address: '',
      additionalRequirements: ''
    });
  };

  const categoryProducts = formData.productCategory 
    ? products.filter(p => p.category === formData.productCategory)
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6">
            <FileText className="w-8 h-8 text-slate-900" />
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">
            Request a Quote
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Get a customized quote for your construction project. Our team will review your requirements and provide competitive pricing within 24 hours.
          </p>
        </div>
      </section>

      {/* Quote Request Form */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Project Information
              </h2>
              <p className="text-slate-600">
                Please provide detailed information about your project to help us prepare an accurate quote.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder="john@company.co.nz"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder="+64 9 XXX XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-slate-900 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder="Company Ltd"
                    />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
                  Project Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="projectType" className="block text-sm font-semibold text-slate-900 mb-2">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                    >
                      <option value="">Select project type</option>
                      <option value="new-construction">New Construction</option>
                      <option value="renovation">Renovation</option>
                      <option value="repair">Repair & Maintenance</option>
                      <option value="commercial">Commercial Building</option>
                      <option value="residential">Residential Building</option>
                      <option value="industrial">Industrial Facility</option>
                      <option value="infrastructure">Infrastructure</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-semibold text-slate-900 mb-2">
                      Project Location *
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      placeholder="City, Region"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectDescription" className="block text-sm font-semibold text-slate-900 mb-2">
                      Project Description *
                    </label>
                    <textarea
                      id="projectDescription"
                      name="projectDescription"
                      value={formData.projectDescription}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all resize-none"
                      placeholder="Describe your project, area to be covered, surface type, and specific requirements..."
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="estimatedQuantity" className="block text-sm font-semibold text-slate-900 mb-2">
                        Estimated Area / Quantity
                      </label>
                      <input
                        type="text"
                        id="estimatedQuantity"
                        name="estimatedQuantity"
                        value={formData.estimatedQuantity}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                        placeholder="e.g., 500 m², 200 L"
                      />
                    </div>

                    <div>
                      <label htmlFor="projectTimeline" className="block text-sm font-semibold text-slate-900 mb-2">
                        Project Timeline *
                      </label>
                      <select
                        id="projectTimeline"
                        name="projectTimeline"
                        value={formData.projectTimeline}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                      >
                        <option value="">Select timeline</option>
                        <option value="urgent">Urgent (1-2 weeks)</option>
                        <option value="1-month">Within 1 Month</option>
                        <option value="1-3-months">1-3 Months</option>
                        <option value="3-6-months">3-6 Months</option>
                        <option value="6-months-plus">6+ Months</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Selection */}
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
                  Product Selection
                </h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="productCategory" className="block text-sm font-semibold text-slate-900 mb-2">
                      Product Category *
                    </label>
                    <select
                      id="productCategory"
                      name="productCategory"
                      value={formData.productCategory}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                    >
                      <option value="">Select a category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.slug}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {categoryProducts.length > 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-900 mb-3">
                        Specific Products (Optional)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {categoryProducts.map(product => (
                          <label
                            key={product.id}
                            className="flex items-start space-x-3 p-4 border-2 border-slate-200 rounded-lg hover:border-slate-400 cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={formData.specificProducts.includes(product.id)}
                              onChange={() => handleProductSelection(product.id)}
                              className="mt-1 w-4 h-4 text-slate-900 border-slate-300 rounded focus:ring-slate-900"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 text-sm">{product.name}</div>
                              <div className="text-xs text-slate-600 mt-1">{product.shortDescription}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label htmlFor="additionalRequirements" className="block text-sm font-semibold text-slate-900 mb-2">
                      Additional Requirements or Questions
                    </label>
                    <textarea
                      id="additionalRequirements"
                      name="additionalRequirements"
                      value={formData.additionalRequirements}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all resize-none"
                      placeholder="Any special requirements, technical questions, or additional information..."
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold text-lg group"
              >
                <Send className="mr-2 w-5 h-5" />
                Submit Quote Request
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Why Request a Quote from EVERKAST NZ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div>
              <div className="w-14 h-14 bg-slate-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Quality Products</h3>
              <p className="text-sm text-slate-600">Premium paints and coatings for professional results</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-slate-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Product Guidance</h3>
              <p className="text-sm text-slate-600">Help choosing the right products for your project</p>
            </div>
            <div>
              <div className="w-14 h-14 bg-slate-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Quick Response</h3>
              <p className="text-sm text-slate-600">Prompt quotes delivered within 24 hours</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    );
};

export default RequestQuote